import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import VendorFilters from "./VendorFilters";
import VendorTable from "./VendorTable";
import VendorDialog from "./VendorDialog";
import ConfirmDialog from "./ConfirmDialog";
import SecondaryDialog from "./SecondaryDialog";
import { useVendors } from "../hooks/useVendors";
import { useCities } from "../hooks/useCities";
import { useVendorForm } from "../hooks/useVendorForm";
import ContactDialog from "./ContactDialog";
import { deleteVendor, getAllContacts } from "../api";
import { useAppDispatch, useAppSelector } from "../hooks";
import { setContacts } from "../features/appSlice";
import NotificationService from "../components/notification/NotificationService";

// Custom theme
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#715939" },
    secondary: { main: "#c09c72" },
    background: { default: "#faead7", paper: "#ffffff" },
    text: { primary: "#191509", secondary: "#8b6d46" },
  },
  typography: { allVariants: { color: "#191509" } },
});

const AdminTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    vendors,
    sortedFilteredVendors,
    setVendors,
    setFilterCity,
    setFilterType,
    filterCity,
    filterType,
  } = useVendors();
  const { allCities, zip, handleZipChange } = useCities();
  const { selectedVendor, setSelectedVendor, isNew, setIsNew, handleChange } =
    useVendorForm();
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [showSecondaryDialog, setShowSecondaryDialog] = useState(false);
  const [mainVendor, setMainVendor] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState<
    string | undefined
  >(undefined);

  const vendorTypes = useAppSelector((state) => state.app.vendorTypes);
  const isDesktop = useMediaQuery("(min-width: 720px)");

  const sortedVendorTypes = useMemo(() => {
    return [...vendorTypes].sort((a, b) => a.name.localeCompare(b.name));
  }, [vendorTypes]);

  useEffect(() => {
    getAllContacts().then((res) => {
      dispatch(setContacts(res));
    });
  }, []);

  const handleAddVendor = () => {
    setSelectedVendor({
      name: "",
      located: "",
      visible: true,
      contact: { id: crypto.randomUUID(), name: "", phone: "", email: "" },
    });
    setIsNew(true);
    setOpenDialog(true);
  };

  const handleLogout = () => {
    supabase.auth.signOut();
    navigate("/", { preventScrollReset: false });
  };

  const handleAddContact = () => {
    setOpenContactDialog(true);
  };

  const deleteVendorHandler = (id?: string) => {
    if (!id) return;
    deleteVendor(id)
      .then(() => {
        setVendors(vendors.filter((v) => v.id !== selectedIdToDelete));
        setOpenDelete(false);
        NotificationService.showToast({
          severity: "success",
          text: "Leverandøren blev slettet",
        });
      })
      .catch((e) => {
        NotificationService.showToast({
          severity: "error",
          text: "Kunne ikke slette leverandør",
        });
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <ContactDialog
        open={openContactDialog}
        onClose={() => setOpenContactDialog(false)}
      />
      <ConfirmDialog
        message="Er du sikker på at du vil slette denne leverandør?"
        title="Er du sikker?"
        open={openDelete && !!selectedIdToDelete}
        onCancel={() => {
          setOpenDelete(false);
          setSelectedIdToDelete(undefined);
        }}
        onConfirm={() => deleteVendorHandler(selectedIdToDelete)}
      />

      {showSecondaryDialog && mainVendor && (
        <SecondaryDialog
          mainVendor={mainVendor}
          onClose={() => setShowSecondaryDialog(false)}
        />
      )}

      <Box p={3} bgcolor={theme.palette.background.default} minHeight="100vh">
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h4">Leverandører</Typography>
            {isDesktop && (
              <Stack direction="row" spacing={2}>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleAddContact}
                >
                  Ny kontaktperson
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleAddVendor}
                >
                  Ny Leverandør
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLogout}
                >
                  Log ud
                </Button>
              </Stack>
            )}
          </Stack>

          <VendorFilters
            filterCity={filterCity}
            filterType={filterType}
            vendors={vendors}
            setFilterCity={setFilterCity}
            setFilterType={setFilterType}
          />
        </>

        <VendorTable
          vendors={sortedFilteredVendors}
          vendorTypes={sortedVendorTypes}
          setSelectedVendor={setSelectedVendor}
          setIsNew={setIsNew}
          setOpenDialog={setOpenDialog}
          setMainVendor={setMainVendor}
          setShowSecondaryDialog={setShowSecondaryDialog}
          setOpenDelete={setOpenDelete}
          setSelectedIdToDelete={setSelectedIdToDelete}
        />

        {selectedVendor && (
          <VendorDialog
            open={openDialog}
            vendor={selectedVendor}
            isNew={isNew}
            onClose={() => setOpenDialog(false)}
            handleChange={handleChange}
            zip={zip}
            handleZipChange={handleZipChange}
            setSelectedVendor={setSelectedVendor}
            vendorTypes={sortedVendorTypes}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default AdminTable;
